import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import { ColumnDef } from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';

import { setHours, setMinutes } from 'date-fns';
import {
    GenericEnum,
    typeQueryOptions,
} from '../../communs/enums/generic-enum';
import { FormInputProps } from '../../componets/form/formInterfaces';
import { cpfAndCnpjMask } from '../../componets/form/mask/cpfAndCnpj';
import { IconComponent, icons } from '../../componets/icons';
import { IFormInputPropsFilter } from '../../componets/interfaces/formInputPropsFilter';
import { ImperativeHandleAccessedByParentListComponent } from '../../componets/interfaces/listProps';
import ListComponet from '../../componets/list';
import { paths, rowsPerPageOptions } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { useContextGlobal } from '../../context/ContextGlobal';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import {
    formatDateWithoutHours,
    momentZoneToDateAddDays,
    momentZoneToUnix
} from '../../util/dateUtil';
import { message } from '../../util/handleMessages';
import { formatNumber } from '../../util/infoFormat';
import IDescription from '../../util/interfaces/description';
import IName from '../../util/interfaces/name';
import {
    objToQuery,
    queryToObj
} from '../../util/query';
import './styles.scss';
import { setTypeUserInQuery } from '../../util/configTypeUser';

interface IInvoiceDetail {
    formPayment: string;
    typePayment: string;
    description: string;
    dueDate: Date;
    installmentValue: number;
    downloaded: boolean;
    legalProcess?: {
        id: string;
        code: string;
        customer: {
            id: string;
            name: string;
            identification: string;
        };
    };
    additionalHonorarium?: {
        id: string;
        code: string;
        customer: {
            id: string;
            name: string;
            identification: string;
        };
    };
}

interface IDataQuery {
    dateInitial?: Date;
    dateFinal?: Date;
    customer?: IName;
    user: IName[];
    typeQuery: IDescription;
    processNumber: string;
    code: string;
}

const defaultValues = {
    code: '',
    processNumber: '',
    customer: {
        id: '',
        name: '',
    },
    typeQuery: {
        id: 'additionalHonorarium',
        description: 'Honorários',
    },
    user: [],
    dateInitial: momentZoneToDateAddDays({ days: -90, startOfType: 'day' }),
    dateFinal: momentZoneToDateAddDays({ days: 0, endOfType: 'day' }),
};

const typeQueryInstance = new GenericEnum(typeQueryOptions);
const typeQueryEnum = typeQueryInstance.optionsList();

const settings = {
    pathRegister: paths.additionalHonorariumRegister,
    pathList: paths.additionalHonorarium,
    apiPath: 'additional-honorarium/without-invoices',
    permissionAdmin: 'invoice_detail_execute',
    title: 'Gerar faturas',
    entity: 'additionalHonorarium',
};

const ListEntity: React.FC = () => {
    const refForm =
        React.useRef<ImperativeHandleAccessedByParentListComponent>(null);
    const [rows, setRows] = useState<IInvoiceDetail[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [toggleSearch, setToggleSearch] = useState<boolean>(false);
    const [deleteRegister, setDeleteRegister] = useState<boolean>(false);
    const [options, setOptions] = useState<
        { id: string; description: string }[]
    >([]);
    const [optionsUsers, setOptionsUsers] = useState<
        { id: string; description: string }[]
    >([]);
    const { user } = useAuth();
    const [customSettings, setCustomSettings] = useState<any>({});

    const history = useHistory();
    const { search } = useLocation();
    const { setOpenLoading } = useContextGlobal();
    const { addToast } = useToast();

    const { control, setValue, getValues, getFieldState } = useForm<IDataQuery>(
        {
            defaultValues,
        },
    );

    useEffect(() => {
        async function loadUsers() {
            try {
                let queryAdd = '';

                if (user && !user.permissions[settings.permissionAdmin]) {
                    queryAdd = `?user.id=${user.sub}`;
                }
                const response = await api.get(`users${queryAdd}`);
                const users = response.data.data.map((u: any) => ({
                    id: u.id,
                    description: u.name,
                }));

                const findUser = users.find((u: IName) => u.id == user.sub);
                setValue('user', [findUser]);

                setOptionsUsers(users);
            } catch (error) {
                addToast({
                    type: 'error',
                    title: message.error.selectAll,
                    description: '',
                });
                setOpenLoading(false);
            }
        }
        loadUsers();
    }, []);

    const loadRows = async (queryString: string): Promise<void> => {
        setOpenLoading(true);
        try {
            let path = `additional-honorarium/without-invoices?${queryString}`;
            const queryObj = queryToObj(queryString);
            if (queryObj?.typeQuery === 'legalProcess') {
                path = `legal-process/without-invoices?${queryString}`;
            }
            if (queryObj?.typeQuery === 'additionalExpense') {
                path = `additional-expense/without-invoices?${queryString}`;
            }
            const response = await api.get(path);
            setRows(response.data.data);
            setTotal(response.data.count);
            setOpenLoading(false);
            if (deleteRegister) {
                setDeleteRegister(false);
            }
        } catch (error) {
            addToast({
                type: 'error',
                title: message.error.selectAll,
                description: '',
            });
            setOpenLoading(false);
        }
    };

    // HANDLER LOAD NEW QUERY FILTER
    const handleByOnKeyPressEnterNewQuery = () => {
        if (getFieldState('dateInitial').invalid) {
            return;
        }
        if (getFieldState('dateFinal').invalid) {
            return;
        }

        let user = '';
        if (getValues('user')?.length > 0) {
            user = getValues('user')
                .map((o: any) => o.id)
                .join(',');
        }

        const queryObj = queryToObj(search.replace('?', ''));
        const newQuery = {
            ...queryObj,
            code: getValues('code'),
            processNumber: getValues('processNumber'),
            'customer.id': getValues('customer')?.id || '',
            'user.id.array': user,
            dateInitial: momentZoneToUnix(getValues('dateInitial')),
            dateFinal: momentZoneToUnix(getValues('dateFinal')),
            typeQuery: getValues('typeQuery')?.id || '',
            page: 1,
        };
        const nQuery = objToQuery(newQuery);
        history.push(`?${nQuery}`);
        loadRows(nQuery);

        if (window.screen.width < 900) {
            setToggleSearch(false);
        }
    };

    const columnsDetPag = React.useMemo<ColumnDef<Object>[]>(
        () => [
            {
                accessorKey: 'date',
                cell: (cell: any) => {
                    return (
                        <TableCell
                            align="left"
                            onClick={() =>
                                handleClick(
                                    cell.row.original.id,
                                    cell.row.original.typeQuery,
                                    cell.row.original.user.id,
                                )
                            }>
                            {formatDateWithoutHours(cell?.getValue())}
                        </TableCell>
                    );
                },
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Data
                    </TableCell>
                ),
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Código / Número
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                handleClick(
                                    row.original.id,
                                    row.original.typeQuery,
                                    row.original.user.id,
                                )
                            }>
                            {row.original?.processNumber || row.original?.code}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: (info: any) => {
                    return (
                        <TableCell align={'left'} padding={'normal'}>
                            Cliente
                        </TableCell>
                    );
                },
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                handleClick(
                                    row.original.id,
                                    row.original.typeQuery,
                                    row.original.user.id,
                                )
                            }>
                            {row.original?.customer?.name}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Identificação
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            key={row.id}
                            align="left"
                            onClick={() =>
                                handleClick(
                                    row.original.id,
                                    row.original.typeQuery,
                                    row.original.user.id,
                                )
                            }>
                            {row.original?.customer
                                ? cpfAndCnpjMask(
                                      row.original?.customer?.identification,
                                      false,
                                  )
                                : ''}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Advogado
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            key={row.id}
                            align="left"
                            onClick={() =>
                                handleClick(
                                    row.original.id,
                                    row.original.typeQuery,
                                    row.original.user.id,
                                )
                            }>
                            {row.original?.user?.name || ''}
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'total',
                cell: (cell: any) => (
                    <TableCell
                        align="left"
                        onClick={() =>
                            handleClick(
                                cell.row.original.id,
                                cell.row.original.typeQuery,
                                cell.row.original.user.id,
                            )
                        }>
                        {formatNumber(cell?.getValue())}
                    </TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Valor
                    </TableCell>
                ),
            },
            {
                id: 'select',
                header: ({ table }: any) => <TableCell align="center" />,
                cell: ({ row }: any) => (
                    <TableCell align="right">
                        <Tooltip
                            title="Gerar parcelas"
                            onClick={() => {
                                handleClick(
                                    row.original.id,
                                    row.original.typeQuery,
                                    row.original.user.id,
                                );
                            }}>
                            <IconButton>
                                <IconComponent icon={icons.EventNote} />
                            </IconButton>
                        </Tooltip>
                    </TableCell>
                ),
            },
        ],
        [],
    );

    const handleClick = (id: string, typeQuery: string, userId: string) => {
        handleEdit(id, typeQuery, userId);
    };

    const handleOnKeyPress = (data: any) => {
        if (data.charCode === 13) {
            handleByOnKeyPressEnterNewQuery();
        }
    };

    const updateAutocompleteCustomer = async (description: any) => {
        if (description && description.length > 0) {
            const response = await api.get(`customer?name=${description}`);
            setOptions(response.data.data);
        }
    };

    const onChangeCustomer = (
        event: React.ChangeEvent<HTMLInputElement>,
        option: any,
    ) => {
        if (!option) {
            updateAutocompleteCustomer(event.target.value);
        }

        if (event.type === 'click' || event.type === 'keydown') {
            handleByOnKeyPressEnterNewQuery();
        }
    };

    const onChangeOperation = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.type === 'click' || event.type === 'keydown') {
            handleByOnKeyPressEnterNewQuery();
        }
    };

    const onChangeInputMultiple = (
        event: React.ChangeEvent<HTMLInputElement>,
        option: any,
    ) => {
        handleByOnKeyPressEnterNewQuery();
    };

    const QUERY_DEFAULT = `page=1&limit=${
        rowsPerPageOptions[0]
    }&dateInitial=${momentZoneToUnix(
        defaultValues.dateInitial,
    )}&dateFinal=${momentZoneToUnix(defaultValues.dateFinal)}&user.id.array=${
        user.sub
    }&typeQuery=additionalHonorarium`;

    const mainInputsFilter = React.useMemo<FormInputProps[]>(
        () => [
            {
                typeInput: 'dateHour',
                name: 'dateInitial',
                control: null,
                label: 'Date de início',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 0);
                    newTime = setMinutes(newTime, 1);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateInitial',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
            {
                typeInput: 'dateHour',
                name: 'dateFinal',
                control: null,
                label: 'Date de fim',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 23);
                    newTime = setMinutes(newTime, 59);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateFinal',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
        ],
        [],
    );

    const inputsFilter: IFormInputPropsFilter[] = [
        {
            typeInput: 'text',
            name: 'processNumber.ilike',
            variant: 'standard',
            control: null,
            label: 'Número do processo',
            setValue,
            handleOnKeyPress: (data: any, name?: string) => {
                if (data.charCode === 13 && name) {
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        data.target.value,
                        name,
                    );
                }
            },
        },
        {
            typeInput: 'autocomplete',
            name: 'customer.id',
            variant: 'standard',
            control: null,
            label: 'Cliente',
            handleOnKeyPress,
            loadingAutocomplete: false,
            setValue: null,
            handleChange: (event: any, option: any, name: string) => {
                if (event.type === 'click' || event.type === 'keydown') {
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        option?.id,
                        name,
                    );
                }
            },
            activeDebounce: true,
            query: (value: string) => `customer?name=${value}`,
        },
        {
            typeInput: 'multiple',
            name: 'user.id.array',
            variant: 'standard',
            control: null,
            label: 'Advogado',
            handleChange: (event: any, option: any[]) => {
                if (event.type === 'click' || event.type === 'keydown') {
                    const data = option?.map(o => o.id);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        data.length > 0 ? data : null,
                        'user.id.array',
                    );
                }
            },
            setValue: null,
            entityPath: 'users',
            keyQuery: 'user.id.array',
            activeDebounce: true,
            query: (value: string) => `users?${setTypeUserInQuery(user.typeUser)}&limit=${value}`,
            DAODataList: (value: any) => {
                return {
                    id: value.id,
                    description: value.name,
                };
            },
        },
        {
            typeInput: 'autocomplete',
            name: 'typeQuery',
            variant: 'standard',
            control: null,
            loadingAutocomplete: false,
            setValue: setValue,
            label: 'Operação',
            options: typeQueryEnum,
            handleChange: (event: any, option: any, name: string) => {
                if (event.type === 'click' || event.type === 'keydown') {
                    console.log(option?.id);
                    const configTypeQuery = {
                        additionalHonorarium:
                            'additional-honorarium/without-invoices',
                        additionalExpense:
                            'additional-expense/without-invoices',
                    };
                    setCustomSettings({
                        ...settings,
                        apiPath:
                            configTypeQuery[
                                option.id as
                                    | 'additionalHonorarium'
                                    | 'additionalExpense'
                            ],
                    });
                    console.log({
                        ...settings,
                        apiPath:
                            configTypeQuery[
                                option.id as
                                    | 'additionalHonorarium'
                                    | 'additionalExpense'
                            ],
                    });

                    setTimeout(() => {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            option?.id,
                            name,
                        );
                    }, 300);
                }
            },
        },
        {
            typeInput: 'text',
            name: 'code.equal',
            variant: 'standard',
            control: null,
            label: 'Código',
            handleOnKeyPress: (data: any, name?: string) => {
                if (data.charCode === 13 && name) {
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        data.target.value,
                        name,
                    );
                }
            },
        },
    ];

    const handleEdit = (id: string, typeQuery: string, userId: string) => {
        const redirect = Object.keys(paths).find(
            k => k === 'invoiceDetailList',
        );
        history.push(
            `${paths.invoiceDetailRegister}/${id}/${typeQuery}/${userId}/${redirect}`,
        );
    };

    return (
        <ListComponet
            settings={settings}
            defaultValues={defaultValues}
            columnsTable={columnsDetPag}
            inputsFilter={inputsFilter}
            mainInputsFilter={mainInputsFilter}
            QUERY_DEFAULT={QUERY_DEFAULT}
            ref={refForm}
            customSettings={customSettings}
        />
    );
    // return (
    //     <div className="principal-container">
    //         <AlertDialog
    //             handleConfirmation={handleConfirmeDelete}
    //             open={openModalDelete}
    //         />
    //         <Box className={toggleSearch ? 'container-box' : ''}>
    //             <Paper sx={{ width: '100%', mb: 2 }} component="div">
    //                 <div className="header-list">
    //                     <div className="header-list-title">
    //                         <Typography
    //                             sx={{ flex: '1 1 100%' }}
    //                             variant="h6"
    //                             id="tableTitle"
    //                             component="div">
    //                             Gerar faturas
    //                         </Typography>
    //                     </div>

    //                     <div className="header-list-search">
    //                         <Stack spacing={2} direction="row" width={'100%'}>
    //                             <Grid container spacing={2}>
    //                                 <Grid item md={6} xs={12}>
    //                                     <FormInputDateWithoutHour
    //                                         name={inputsFilter[0].name}
    //                                         control={inputsFilter[0].control}
    //                                         rules={null}
    //                                         label={inputsFilter[0].label}
    //                                         setValue={inputsFilter[0].setValue}
    //                                         fullWidth={
    //                                             inputsFilter[0].fullWidth ||
    //                                             false
    //                                         }
    //                                         setError={inputsFilter[0].setError}
    //                                         clearErrors={
    //                                             inputsFilter[0].clearErrors
    //                                         }
    //                                         handleChange={
    //                                             inputsFilter[0].handleChange
    //                                         }
    //                                     />
    //                                 </Grid>
    //                                 <Grid item md={6} xs={12}>
    //                                     <FormInputDateWithoutHour
    //                                         name={inputsFilter[1].name}
    //                                         control={inputsFilter[1].control}
    //                                         rules={null}
    //                                         label={inputsFilter[1].label}
    //                                         setValue={inputsFilter[1].setValue}
    //                                         fullWidth={
    //                                             inputsFilter[1].fullWidth ||
    //                                             false
    //                                         }
    //                                         setError={inputsFilter[1].setError}
    //                                         clearErrors={
    //                                             inputsFilter[1].clearErrors
    //                                         }
    //                                         handleChange={
    //                                             inputsFilter[1].handleChange
    //                                         }
    //                                     />
    //                                 </Grid>
    //                             </Grid>
    //                             <Tooltip
    //                                 title="Opções de filtros"
    //                                 onClick={() => setToggleSearch(true)}>
    //                                 <IconButton data-qa="filter-options">
    //                                     <FilterListIcon />
    //                                 </IconButton>
    //                             </Tooltip>
    //                         </Stack>
    //                     </div>
    //                 </div>
    //                 <TableGeneric
    //                     data={rows}
    //                     columns={columnsDetPag}
    //                     minWidth={650}
    //                 />
    //                 <TablePagination
    //                     rowsPerPageOptions={rowsPerPageOptions}
    //                     component="div"
    //                     count={total}
    //                     labelRowsPerPage={'Linhas por página'}
    //                     labelDisplayedRows={defaultLabelDisplayedRows}
    //                     rowsPerPage={rowsPerPage}
    //                     page={total > 0 ? page : 0}
    //                     onPageChange={(event: unknown, newPage: number) =>
    //                         handleChangePage(
    //                             event,
    //                             newPage,
    //                             setPage,
    //                             history,
    //                             loadRows,
    //                         )
    //                     }
    //                     onRowsPerPageChange={(
    //                         event: React.ChangeEvent<HTMLInputElement>,
    //                     ) =>
    //                         handleChangeRowsPerPage(
    //                             event,
    //                             setRowsPerPage,
    //                             setPage,
    //                             history,
    //                             loadRows,
    //                         )
    //                     }
    //                 />
    //             </Paper>
    //         </Box>

    //         <FilterSearchComponent
    //             inputs={inputsFilter}
    //             setToggleSearch={setToggleSearch}
    //             toggleSearch={toggleSearch}
    //             setRowsPerPage={setRowsPerPage}
    //             setPage={setPage}
    //             rowsPerPage={rowsPerPage}
    //             page={page}
    //             implementationLoadRows={loadRows}
    //             updateRows={deleteRegister}
    //             setUpdateRows={setDeleteRegister}
    //             queryDefault={QUERY_DEFAULT}
    //         />
    //     </div>
    // );
};

export default ListEntity;
